/**
 * PostManagerPage — admin oversight of ALL community posts (any visibility).
 * Search + paginated table + delete + CSV/PDF export.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FileText, Search, Download, Trash2, ChevronLeft, ChevronRight, X, ThumbsUp, MessageCircle, Eye, TrendingUp, BarChart3, ExternalLink } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { adminService, type AdminPostRow, type ContentCounts } from '@/services/adminService'
import { exportToCSV, exportToPDF, type ExportColumn } from '@/lib/exportUtils'
import { GovLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout'

const visStyles: Record<string, string> = {
  PUBLIC: 'bg-green-100 text-green-700',
  FRIENDS: 'bg-amber-100 text-amber-700',
  PRIVATE: 'bg-gray-100 text-gray-700',
}

// Overview stat card — mirrors the Strishakti superadmin reel/post overview
// grid (gradient icon tile + label + big number).
const StatCard: React.FC<{ label: string; value: number; icon: React.ElementType; gradient: string }> = ({
  label, value, icon: Icon, gradient,
}) => (
  <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3 h-full">
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br ${gradient} shrink-0`}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-xs text-gray-500 leading-tight">{label}</div>
      <div className="text-lg font-bold text-gray-900 leading-tight mt-0.5">{value.toLocaleString()}</div>
    </div>
  </div>
)

const PostManagerPage: React.FC = () => {
  const { user, isAuthenticated } = useAuthStore()
  const navigate = useNavigate()

  const [rows, setRows] = useState<AdminPostRow[]>([])
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [totalElements, setTotalElements] = useState(0)
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<number | null>(null)
  const [counts, setCounts] = useState<ContentCounts | null>(null)
  const [viewPost, setViewPost] = useState<AdminPostRow | null>(null)
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!isAuthenticated) return navigate('/login')
    if (user && user.role !== 'ADMIN' && user.role !== 'SUPER_ADMIN') navigate('/feed')
  }, [user, isAuthenticated, navigate])

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => { setDebounced(search); setPage(0) }, 300)
    return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current) }
  }, [search])

  const fetch = useCallback(async () => {
    if (!user) return
    setLoading(true)
    try {
      const data = await adminService.getAdminPosts(debounced, page, 20)
      setRows(data.content)
      setTotalPages(data.totalPages)
      setTotalElements(data.totalElements)
    } catch (e) {
      console.error('fetch posts', e)
    } finally {
      setLoading(false)
    }
  }, [debounced, page, user])

  const fetchCounts = useCallback(async () => {
    try { setCounts(await adminService.getAdminPostCounts()) }
    catch (e) { console.error('fetch post counts', e) }
  }, [])

  useEffect(() => { fetch() }, [fetch])
  useEffect(() => { fetchCounts() }, [fetchCounts])

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this post permanently? This cannot be undone.')) return
    setBusy(id)
    try { await adminService.deleteAdminPost(id); fetch(); fetchCounts() }
    catch (e) { console.error('delete post', e); alert('Failed to delete post.') }
    finally { setBusy(null) }
  }

  const exportColumns: ExportColumn<AdminPostRow>[] = [
    { header: 'ID', key: 'id' },
    { header: 'Author', key: 'userName' },
    { header: 'Content', key: 'content', format: (v) => (v ? String(v).replace(/\s+/g, ' ').slice(0, 200) : '') },
    { header: 'Type', key: 'postType' },
    { header: 'Visibility', key: 'visibility' },
    { header: 'Likes', key: 'likeCount' },
    { header: 'Comments', key: 'commentCount' },
    { header: 'Shares', key: 'shareCount' },
    { header: 'Views', key: 'viewCount' },
    { header: 'Created', key: 'createdAt', format: (v) => new Date(v as string).toLocaleString() },
  ]

  return (
    <GovLayout>
      <div className="space-y-4">
        <GovPageTitle title="Post Manager" subtitle={`${totalElements.toLocaleString()} post${totalElements === 1 ? '' : 's'}`} icon={FileText} />

        {/* Overview stat cards — Total / Views / Likes / Comments / Today / Avg */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard label="Total Posts" value={counts?.total ?? 0} icon={FileText} gradient="from-purple-500 to-violet-600" />
          <StatCard label="Total Views" value={counts?.totalViews ?? 0} icon={Eye} gradient="from-blue-500 to-indigo-600" />
          <StatCard label="Total Likes" value={counts?.totalLikes ?? 0} icon={ThumbsUp} gradient="from-pink-500 to-rose-600" />
          <StatCard label="Comments" value={counts?.totalComments ?? 0} icon={MessageCircle} gradient="from-emerald-500 to-green-600" />
          <StatCard label="Today" value={counts?.today ?? 0} icon={TrendingUp} gradient="from-orange-500 to-amber-600" />
          <StatCard label="Avg Views" value={counts?.avgViews ?? 0} icon={BarChart3} gradient="from-cyan-500 to-teal-600" />
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
          <span className="font-medium text-gray-500">Visibility:</span>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 text-green-700">Public {counts?.publicCount ?? 0}</span>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-700">Friends {counts?.friendsCount ?? 0}</span>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100 text-gray-700">Private {counts?.privateCount ?? 0}</span>
        </div>

        <GovCard>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search post content…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-10 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <GovButton variant="secondary" size="sm" onClick={() => exportToCSV('posts', exportColumns, rows)} disabled={!rows.length}>
                <Download className="w-4 h-4 inline mr-1" /> CSV
              </GovButton>
              <GovButton variant="secondary" size="sm" onClick={() => exportToPDF('posts', 'Posts Report', exportColumns, rows)} disabled={!rows.length}>
                <FileText className="w-4 h-4 inline mr-1" /> PDF
              </GovButton>
            </div>
          </div>

          {loading ? (
            <p className="text-sm text-gray-500 p-8 text-center">Loading…</p>
          ) : rows.length === 0 ? (
            <GovEmptyState icon="📝" title="No Posts Found" description={search ? 'Try a different search.' : 'No posts have been created yet.'} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">Author</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">Content</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">Vis.</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">Engagement</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-600">Created</th>
                    <th className="text-right p-4 text-sm font-semibold text-gray-600">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p) => (
                    <tr key={p.id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="p-4 font-medium text-gray-900 whitespace-nowrap">{p.userName || '—'}</td>
                      <td className="p-4 text-sm text-gray-700 max-w-xs truncate">{p.content || <span className="text-gray-400">(no text)</span>}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${visStyles[p.visibility] || visStyles.PRIVATE}`}>{p.visibility}</span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-3 text-xs text-gray-600">
                          <span className="inline-flex items-center gap-1" title="Likes"><ThumbsUp className="w-3.5 h-3.5" />{p.likeCount}</span>
                          <span className="inline-flex items-center gap-1" title="Comments"><MessageCircle className="w-3.5 h-3.5" />{p.commentCount}</span>
                          <span className="inline-flex items-center gap-1" title="Views"><Eye className="w-3.5 h-3.5" />{p.viewCount}</span>
                        </div>
                      </td>
                      <td className="p-4 text-sm text-gray-600 whitespace-nowrap">{new Date(p.createdAt).toLocaleDateString()}</td>
                      <td className="p-4 text-right">
                        <div className="inline-flex gap-2">
                          <button
                            onClick={() => setViewPost(p)}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded-lg"
                          >
                            <Eye className="w-3.5 h-3.5" /> View
                          </button>
                          <button
                            onClick={() => handleDelete(p.id)}
                            disabled={busy === p.id}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                          >
                            <Trash2 className="w-3.5 h-3.5" /> {busy === p.id ? '…' : 'Delete'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t border-gray-100">
                <GovButton variant="secondary" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>
                  <ChevronLeft className="w-4 h-4" /> Prev
                </GovButton>
                <span className="text-sm text-gray-600">Page {page + 1} of {Math.max(1, totalPages)} · {totalElements} total</span>
                <GovButton variant="secondary" size="sm" onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages - 1}>
                  Next <ChevronRight className="w-4 h-4" />
                </GovButton>
              </div>
            </div>
          )}
        </GovCard>
      </div>

      {/* Post detail modal — View opens an in-page popup (no redirect) with the
          same row actions (Delete) plus an "Open on site" link to the full page. */}
      {viewPost && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
          onClick={() => setViewPost(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-xl">
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-600" /> Post #{viewPost.id}
              </h3>
              <button onClick={() => setViewPost(null)} className="p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium text-gray-900">{viewPost.userName || '—'}</span>
                {viewPost.postType && <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs">{viewPost.postType}</span>}
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${visStyles[viewPost.visibility] || visStyles.PRIVATE}`}>{viewPost.visibility}</span>
              </div>
              <p className="text-sm text-gray-700 whitespace-pre-wrap break-words bg-gray-50 rounded-lg p-3">
                {viewPost.content || <span className="text-gray-400">(no text)</span>}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                {[
                  { label: 'Views', value: viewPost.viewCount },
                  { label: 'Likes', value: viewPost.likeCount },
                  { label: 'Comments', value: viewPost.commentCount },
                  { label: 'Shares', value: viewPost.shareCount },
                ].map((s) => (
                  <div key={s.label} className="bg-gray-50 rounded-lg p-2">
                    <div className="text-lg font-bold text-gray-900">{s.value}</div>
                    <div className="text-xs text-gray-500">{s.label}</div>
                  </div>
                ))}
              </div>
              <div className="text-xs text-gray-500">Created {new Date(viewPost.createdAt).toLocaleString()}</div>
            </div>
            <div className="flex items-center justify-between gap-2 p-4 border-t border-gray-100 sticky bottom-0 bg-white rounded-b-xl">
              <button
                onClick={() => { navigate(`/post/${viewPost.id}`); setViewPost(null) }}
                className="inline-flex items-center gap-1 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Open on site
              </button>
              <button
                onClick={() => { const id = viewPost.id; setViewPost(null); handleDelete(id) }}
                disabled={busy === viewPost.id}
                className="inline-flex items-center gap-1 px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
              >
                <Trash2 className="w-3.5 h-3.5" /> {busy === viewPost.id ? 'Deleting…' : 'Delete post'}
              </button>
            </div>
          </div>
        </div>
      )}
    </GovLayout>
  )
}

export default PostManagerPage